import { apiClient } from './apiClient'

export interface Language {
  code: string
  name: string
  nativeName: string
  direction: 'ltr' | 'rtl'
  isDefault: boolean
}

class LanguagesApiService {
  private cachedLanguages: Language[] | null = null
  private cacheTimestamp: number = 0
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

  /**
   * Get all enabled languages from API
   */
  async getEnabledLanguages(forceRefresh = false): Promise<Language[]> {
    const now = Date.now()
    
    // Return cached data if still valid
    if (!forceRefresh && this.cachedLanguages && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
      return this.cachedLanguages
    }

    try {
      const response = await apiClient.get<Language[]>('/languages')
      this.cachedLanguages = response.data
      this.cacheTimestamp = now
      return response.data
    } catch (error) {
      console.error('Failed to fetch enabled languages:', error)
      
      // Fallback to default languages if API fails
      if (this.cachedLanguages) {
        return this.cachedLanguages
      }
      
      // Ultimate fallback
      return [
        { code: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl', isDefault: true },
        { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr', isDefault: false }
      ]
    }
  }

  /**
   * Get default language code
   */
  async getDefaultLanguageCode(): Promise<string> {
    try {
      const response = await apiClient.get<{ code: string }>('/languages/default')
      return response.data.code
    } catch (error) {
      console.error('Failed to fetch default language:', error)
      return 'ar' // Fallback
    }
  }

  /**
   * Clear cache (useful when language changes)
   */
  clearCache(): void {
    this.cachedLanguages = null
    this.cacheTimestamp = 0
  }
}

export const languagesApi = new LanguagesApiService()
